import { defineStore } from 'pinia';
import {
  filter, isArray, keyBy, sortBy,
} from 'lodash-es';
import chain from '@/lib/utils/chain';
import {
  GLOSSARY_MODE_TAGS,
  GLOSSARY_DEFAULT_TAG_NAME,
} from '@/lib/constants';
import * as types from '@/lib/constants/store';

// Build the deduped, sorted list of groups from a set of terms.
const buildGroups = (terms) => chain(terms)
  .map((term) => term.groups || [])
  .flatten()
  .uniqBy((group) => group.id)
  .sortBy((group) => group.title)
  .value();

const useGlossaryStore = defineStore('studio-glossary', {
  state: () => ({
    terms: null,
    groups: null,
    glossaryMode: GLOSSARY_MODE_TAGS, // auto, tags
    tagName: GLOSSARY_DEFAULT_TAG_NAME,
    selectedTerm: null,
  }),
  getters: {
    termsByLanguage() {
      return (code) => {
        const termsByCode = filter(this.terms, (term) => term.language_code === code);

        return sortBy(termsByCode, (term) => term?.title?.toLowerCase());
      };
    },
    hasGlossaryTerms() {
      return !!this.terms;
    },
  },
  actions: {
    async [types.LOAD_GLOSSARY_TERM_DETAILS](payload) {
      // Swallow any fetch errors to "no glossary data".
      try {
        const res = await this.deApiFetch(
          '/techbook/vocabulary/terms',
          {
            method: 'POST',
            body: payload,
          },
        );

        if (res?.status === 200) {
          this[types.SET_GLOSSARY_DATA](res.data);

          return res.data;
        }
      } catch {
        // no-op
      }

      return null;
    },
    [types.SET_GLOSSARY_DATA](glossaryData) {
      if (isArray(glossaryData?.terms)) {
        // Merge incoming terms by id: update an existing term's properties in
        // place, or append it when it's new. Existing order is preserved.
        const mergedTerms = [...(this.terms || [])];
        const termsById = keyBy(mergedTerms, (term) => term.id);

        glossaryData.terms.forEach((term) => {
          const existingTerm = termsById[term.id];

          if (existingTerm) {
            Object.assign(existingTerm, term);
          } else {
            termsById[term.id] = term;
            mergedTerms.push(term);
          }
        });

        this.terms = mergedTerms;
        // Rebuild the groups completely from the merged terms.
        this.groups = buildGroups(this.terms);
      }

      if (glossaryData?.mode) {
        this.glossaryMode = glossaryData.mode;
      }

      if (glossaryData?.tagName) {
        this.tagName = glossaryData.tagName;
      }
    },
    [types.SET_GLOSSARY_SELECTED_TERM](term) {
      this.selectedTerm = term;
    },
    [types.CLEAR_GLOSSARY_DATA]() {
      this.terms = null;
      this.groups = null;
      this.selectedTerm = null;
    },
  },
});

export default useGlossaryStore;
